// Letris — App shell: pantallas, perfiles, leaderboard, tienda
import { LetrisGame, LEVEL_STYLES } from './engine.js';
import { MODES, UNLOCKS, COINS_PER_POINT } from './data.js';
import { DuelConn, attackForWord, blockersForWord, updateElo } from './duel.js';
import { submitScore, fetchLeaderboard } from './api.js';
import { shareText, dailyEmojiBlock, renderShareCard, siteUrl } from './share.js';
import WORDS from './palabras.json';

const ROWS = 20, COLS = 10;

const STORAGE_KEYS = {
  profiles: 'letris.profiles',
  active: 'letris.activeProfile',
  leaderboard: 'letris.leaderboard',
  diarioDone: 'letris.diarioDone',
  legalAccepted: 'letris.legalAccepted',
  tutorialSeen: 'letris.tutorialSeen',
};
const LEGAL_VERSION = 'v1';

export const App = {
  profiles: [],
  activeId: null,
  leaderboard: {},
  diarioDone: {},
  dictionary: null,

  init() {
    this.load();
    this.dictionary = new Set(WORDS);
    LetrisGame.init();
    // deep-link: ?challenge=CODE → unirse directo al duelo
    try {
      const params = new URLSearchParams(location.search);
      const challenge = (params.get('challenge') || '').toUpperCase().slice(0, 4);
      if (/^[A-Z0-9]{4}$/.test(challenge)) this._pendingChallenge = challenge;
    } catch {}
    this.show('splash');
  },

  load() {
    try { this.profiles = JSON.parse(localStorage.getItem(STORAGE_KEYS.profiles) || '[]'); } catch { this.profiles = []; }
    this.activeId = localStorage.getItem(STORAGE_KEYS.active) || null;
    try { this.leaderboard = JSON.parse(localStorage.getItem(STORAGE_KEYS.leaderboard) || '{}'); } catch { this.leaderboard = {}; }
    try { this.diarioDone = JSON.parse(localStorage.getItem(STORAGE_KEYS.diarioDone) || '{}'); } catch { this.diarioDone = {}; }
    // migrations
    for (const p of this.profiles) {
      if (p.elo == null) p.elo = 1000;
      if (!p.duelStats) p.duelStats = { wins: 0, losses: 0 };
      if (!p.dailyStreak) p.dailyStreak = { count: 0, best: 0, lastDate: null };
    }
  },
  save() {
    localStorage.setItem(STORAGE_KEYS.profiles, JSON.stringify(this.profiles));
    if (this.activeId) localStorage.setItem(STORAGE_KEYS.active, this.activeId);
    localStorage.setItem(STORAGE_KEYS.leaderboard, JSON.stringify(this.leaderboard));
    localStorage.setItem(STORAGE_KEYS.diarioDone, JSON.stringify(this.diarioDone));
  },
  active() { return this.profiles.find(p => p.id === this.activeId); },

  createProfile(name) {
    const p = {
      id: 'p-' + Math.random().toString(36).slice(2, 9),
      name: name.toUpperCase().slice(0, 12),
      createdAt: Date.now(),
      coins: 50,
      owned: ['theme-default'],
      activeTheme: 'theme-default',
      stats: { games: 0, totalScore: 0, totalWords: 0 },
      bestScores: {},
      elo: 1000,
      duelStats: { wins: 0, losses: 0 },
      dailyStreak: { count: 0, best: 0, lastDate: null },
    };
    this.profiles.push(p);
    this.activeId = p.id;
    this.save();
    return p;
  },
  deleteProfile(id) {
    this.profiles = this.profiles.filter(p => p.id !== id);
    if (this.activeId === id) this.activeId = this.profiles[0]?.id || null;
    this.save();
  },
  setActive(id) { this.activeId = id; this.save(); },

  pushLeaderboard(modeId, entry) {
    if (!this.leaderboard[modeId]) this.leaderboard[modeId] = [];
    this.leaderboard[modeId].push(entry);
    this.leaderboard[modeId].sort((a, b) => b.score - a.score);
    this.leaderboard[modeId] = this.leaderboard[modeId].slice(0, 10);
    this.save();
  },

  show(name, params) {
    document.body.scrollTop = 0;
    document.body.classList.remove('lt-screen-playing');
    this.applyTheme();
    const root = document.getElementById('app');
    root.innerHTML = '';
    (SCREENS[name] || SCREENS.menu)(root, params || {});
  },
  applyTheme() {
    const p = this.active();
    const theme = p ? UNLOCKS[p.activeTheme || 'theme-default'] : null;
    const palette = theme?.palette || null;
    const root = document.documentElement;
    if (!palette) {
      root.style.removeProperty('--bg');
      root.style.removeProperty('--board');
      root.style.removeProperty('--grid');
      root.style.removeProperty('--ink');
      root.style.removeProperty('--ink-dim');
      root.style.removeProperty('--line-red');
      root.style.removeProperty('--neon');
      root.style.removeProperty('--theme-accent-2');
      root.style.removeProperty('--theme-accent-3');
      root.style.removeProperty('--theme-accent-4');
      return;
    }
    const [primary, danger, dark, prize] = palette;
    root.style.setProperty('--bg', dark);
    root.style.setProperty('--board', colorMix(dark, '#000000', 0.48));
    root.style.setProperty('--grid', colorMix(primary, dark, 0.22));
    root.style.setProperty('--ink', '#F8F4EA');
    root.style.setProperty('--ink-dim', colorMix('#F8F4EA', dark, 0.48));
    root.style.setProperty('--line-red', danger);
    root.style.setProperty('--neon', primary);
    root.style.setProperty('--theme-accent-2', danger);
    root.style.setProperty('--theme-accent-3', dark);
    root.style.setProperty('--theme-accent-4', prize);
  },
};

function colorMix(a, b, t) {
  const pa = hexToRgb(a), pb = hexToRgb(b);
  if (!pa || !pb) return a;
  const mix = pa.map((v, i) => Math.round(v * (1 - t) + pb[i] * t));
  return '#' + mix.map(v => v.toString(16).padStart(2, '0')).join('');
}
function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

// expose for console debugging
if (typeof window !== 'undefined') window.App = App;

// =========================================================================
// HELPERS DOM
// =========================================================================
function el(tag, attrs, ...children) {
  const e = document.createElement(tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') e.className = v;
    else if (k === 'style') e.style.cssText = v;
    else if (k.startsWith('on')) e.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'html') e.innerHTML = v;
    else if (v === true) e.setAttribute(k, '');
    else e.setAttribute(k, v);
  }
  for (const c of children.flat(Infinity)) {
    if (c == null || c === false) continue;
    if (typeof c === 'string' || typeof c === 'number') e.appendChild(document.createTextNode(c));
    else e.appendChild(c);
  }
  return e;
}
function shellFrame(root, contents) {
  const frame = el('div', { class: 'lt-app' }, contents);
  root.appendChild(frame);
}
function logoBlock() {
  return el('div', { class: 'lt-logo' },
    el('span', { class: 'lt-logo__main' }, 'LETRIS'),
    el('span', { class: 'lt-logo__sub' }, 'word · tetris'));
}
function topBar(title, opts = {}) {
  const right = el('div', { class: 'lt-controls' });
  if (opts.coins != null) right.appendChild(el('div', { class: 'lt-coin-pill' }, '◈ ', String(opts.coins)));
  if (opts.profile) right.appendChild(el('div', { class: 'lt-profile-pill', onclick: () => App.show('profile-select') }, '◉ ', opts.profile));
  if (opts.back) right.appendChild(el('button', { class: 'lt-icon-btn', onclick: opts.back, title: 'Volver' }, '←'));
  return el('header', { class: 'lt-top' },
    logoBlock(),
    title ? el('div', { class: 'lt-screen-title' }, title) : null,
    right);
}

// =========================================================================
// PANTALLAS
// =========================================================================
const SCREENS = {};

SCREENS.splash = (root) => {
  const enter = () => {
    if (localStorage.getItem(STORAGE_KEYS.legalAccepted) !== LEGAL_VERSION) {
      openLegalGateModal(() => proceedFromSplash());
      return;
    }
    proceedFromSplash();
  };
  shellFrame(root, [
    el('div', { class: 'lt-splash' },
      el('section', { class: 'lt-splash-hero' },
        el('div', { class: 'lt-splash-copy' },
          el('div', { class: 'lt-splash-kicker' }, 'WORD TETRIS EN ESPAÑOL'),
          el('div', { class: 'lt-splash-logo' }, 'LETRIS'),
          el('p', { class: 'lt-splash-line' }, 'Forma palabras, fusiona letras, elige cartas y aguanta fases con bosses cada cinco niveles.'),
          el('div', { class: 'lt-splash-actions' },
            el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', onclick: enter }, 'EMPEZAR'),
            el('button', { class: 'lt-btn lt-btn--ghost lt-btn--big', onclick: () => openTutorialModal() }, 'CÓMO JUGAR')),
          el('div', { class: 'lt-splash-stats' },
            el('span', null, `${WORDS.length.toLocaleString('es')} palabras`),
            el('span', null, '5 fases'),
            el('span', null, 'bosses'),
            el('span', null, 'builds Letris'))
        ),
        splashBoard()
      ),
      el('div', { class: 'lt-splash-footer' },
        el('div', { class: 'lt-splash-credits' }, 'diccionario: JorgeDuenasLerin/diccionario-espanol-txt (MIT)'),
        el('div', { class: 'lt-splash-legal' },
          el('button', { class: 'lt-link', onclick: () => App.show('legal', { tab: 'terms' }) }, 'Términos'),
          el('span', null, ' · '),
          el('button', { class: 'lt-link', onclick: () => App.show('legal', { tab: 'privacy' }) }, 'Privacidad'),
          el('span', null, ' · '),
          el('button', { class: 'lt-link', onclick: () => App.show('legal', { tab: 'cookies' }) }, 'Cookies'),
        )
      )
    )
  ]);
};

function splashBoard() {
  const cells = [
    '', '', 'L', '', '', '', '', '', '', '',
    '', '', 'E', 'T', '', '', '', '▓', '', '',
    '', '', '', 'R', '', '', 'A', 'A', '', '',
    '', 'S', 'O', 'L', '', '', '', 'R', '', '',
    '', '', '', '', '', '✸', '', '', '', '',
    'C', 'A', 'S', 'A', '', '', 'N', '', '', '',
    '', '', '', 'A', '', '', 'I', '', '✦', '',
    '', '', '', '', '', 'F', 'N', '', 'O', '',
    '', 'M', 'E', 'T', 'A', '', '', '', 'S', '',
    '', '', '', '', '', '', '', '', '', '',
  ];
  return el('div', { class: 'lt-splash-board-wrap', 'aria-hidden': 'true' },
    el('div', { class: 'lt-splash-panel lt-splash-panel--boss' },
      el('span', null, 'EL CENSOR'),
      el('strong', null, '28/42')),
    el('div', { class: 'lt-splash-board' },
      ...cells.map((ch, i) => {
        const cls = [
          'lt-splash-cell',
          ch ? 'lt-splash-cell--tile' : '',
          ch === '▓' ? 'lt-splash-cell--block' : '',
          ch === '✸' ? 'lt-splash-cell--bomb' : '',
          ch === '✦' ? 'lt-splash-cell--bonus' : '',
          ['L','E','T','R','I','S'].includes(ch) ? 'lt-splash-cell--sel' : '',
        ].filter(Boolean).join(' ');
        return el('span', { class: cls, style: ch ? `--i:${i};` : '' }, ch);
      }),
      el('div', { class: 'lt-splash-danger' }, '12s')
    ),
    el('div', { class: 'lt-splash-panel lt-splash-panel--cards' },
      el('span', null, 'DRAFT'),
      el('strong', null, '1 DE 3 LETRIS'))
  );
}

function proceedFromSplash() {
  if (App.profiles.length === 0) App.show('profile-create');
  else if (!App.activeId) App.show('profile-select');
  else if (App._pendingChallenge) {
    const code = App._pendingChallenge;
    App._pendingChallenge = null;
    try { history.replaceState(null, '', location.pathname); } catch {}
    App.show('duelo-lobby', { autoJoin: code });
  }
  else {
    App.show('menu');
    if (!localStorage.getItem(STORAGE_KEYS.tutorialSeen)) openTutorialModal({ firstTime: true });
  }
}

SCREENS['profile-create'] = (root) => {
  let name = '';
  const input = el('input', { class: 'lt-input', maxlength: '12', placeholder: 'TU NOMBRE',
    oninput: (e) => { name = e.target.value; }
  });
  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    App.createProfile(trimmed);
    App.show('menu');
    if (!localStorage.getItem(STORAGE_KEYS.tutorialSeen)) openTutorialModal({ firstTime: true });
  };
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });

  shellFrame(root, [
    topBar('NUEVO PERFIL', { back: () => App.show(App.profiles.length ? 'profile-select' : 'splash') }),
    el('div', { class: 'lt-screen-body lt-profile-screen' },
      el('section', { class: 'lt-menu-hero lt-profile-hero lt-profile-hero--create' },
        el('div', { class: 'lt-menu-hero__copy lt-profile-hero__copy' },
          el('div', { class: 'lt-menu-kicker' }, 'NUEVO JUGADOR'),
          el('h1', { class: 'lt-menu-title' }, 'Elige tu nombre y empieza a jugar.'),
          el('div', { class: 'lt-profile-hero__hint' }, 'Tu progreso, ELO y desbloqueos se guardan localmente.')),
        el('div', { class: 'lt-menu-profile-panel lt-profile-hero__side' },
          el('div', { class: 'lt-menu-profile-name' }, '◆ NUEVO ◆'),
          el('div', { class: 'lt-profile-create-perks' },
            el('div', null, '◈', el('span', null, '50 monedas iniciales')),
            el('div', null, '★', el('span', null, 'ELO 1000')),
            el('div', null, '◢', el('span', null, 'Tema clásico desbloqueado')),
            el('div', null, '✦', el('span', null, 'Reto diario disponible'))))
      ),
      el('div', { class: 'lt-profile-create-form' },
        el('div', { class: 'lt-profile-create-card' },
          el('div', { class: 'lt-profile-create-card__label' }, 'NOMBRE DE JUGADOR'),
          input,
          el('div', { class: 'lt-profile-create-card__hint' }, 'Máx. 12 caracteres · solo se guarda en este dispositivo')),
        el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', onclick: submit }, 'CREAR PERFIL'))
    )
  ]);
  setTimeout(() => input.focus(), 50);
};

SCREENS['profile-select'] = (root) => {
  const totalProfiles = App.profiles.length;
  const activeProfile = App.active();
  const heroBest = activeProfile ? Math.max(0, ...Object.values(activeProfile.bestScores || { _: 0 })) : 0;

  const cards = App.profiles.map((p, idx) => {
    const isActive = p.id === App.activeId;
    const best = Math.max(0, ...Object.values(p.bestScores || { _: 0 }));
    const wins = p.duelStats?.wins || 0;
    const losses = p.duelStats?.losses || 0;
    const totalDuels = wins + losses;
    const winrate = totalDuels ? Math.round((wins / totalDuels) * 100) + '%' : '—';
    const accentIdx = idx % 4;
    return el('div', { class: 'lt-mode-card lt-profile-card-v2 lt-profile-card-v2--accent' + accentIdx + (isActive ? ' is-active' : '') },
      el('div', { class: 'lt-mode-card__top' },
        el('div', { class: 'lt-mode-name' }, p.name),
        isActive ? el('div', { class: 'lt-profile-card-v2__badge' }, 'ACTIVO') : el('div', { class: 'lt-mode-best' }, String(best).padStart(5, '0'))),
      el('div', { class: 'lt-profile-card-v2__grid' },
        el('div', null, el('span', null, 'MEJOR'), el('strong', null, String(best))),
        el('div', null, el('span', null, 'ELO'), el('strong', null, String(p.elo || 1000))),
        el('div', null, el('span', null, 'RACHA'), el('strong', null, '🔥 ' + (p.dailyStreak?.count || 0))),
        el('div', null, el('span', null, 'PARTIDAS'), el('strong', null, String(p.stats?.games || 0))),
        el('div', null, el('span', null, 'COINS'), el('strong', null, '◈ ' + p.coins)),
        el('div', null, el('span', null, 'WINRATE'), el('strong', null, winrate))),
      el('div', { class: 'lt-profile-card-v2__actions' },
        el('button', { class: 'lt-btn lt-btn--' + (isActive ? 'ghost' : 'primary'), disabled: isActive,
          onclick: () => { App.setActive(p.id); App.show('menu'); } }, isActive ? 'EN USO' : 'USAR'),
        el('button', { class: 'lt-btn lt-btn--ghost lt-profile-card-v2__del', title: 'Borrar perfil', onclick: () => {
          if (confirm(`¿Borrar perfil ${p.name}?`)) { App.deleteProfile(p.id); App.show('profile-select'); }
        } }, '✕'))
    );
  });

  const addCard = el('button', { class: 'lt-mode-card lt-profile-card-v2 lt-profile-card-v2--add',
    onclick: () => App.show('profile-create') },
    el('div', { class: 'lt-profile-card-v2__plus' }, '+'),
    el('div', { class: 'lt-profile-card-v2__add-label' }, 'NUEVO PERFIL'),
    el('div', { class: 'lt-profile-card-v2__add-hint' }, 'Crea otro jugador'));

  shellFrame(root, [
    topBar('PERFILES', { back: () => App.show(activeProfile ? 'menu' : 'splash'), profile: activeProfile?.name }),
    el('div', { class: 'lt-screen-body lt-profile-screen' },
      el('section', { class: 'lt-menu-hero lt-profile-hero' },
        el('div', { class: 'lt-menu-hero__copy lt-profile-hero__copy' },
          el('div', { class: 'lt-menu-kicker' }, 'JUGADORES LOCALES'),
          el('h1', { class: 'lt-menu-title' }, 'Cambia de perfil o crea uno nuevo.'),
          el('div', { class: 'lt-menu-hero__actions' },
            el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', onclick: () => App.show('profile-create') }, '+ NUEVO PERFIL'),
            activeProfile ? el('button', { class: 'lt-btn lt-btn--ghost lt-btn--big', onclick: () => App.show('menu') }, 'IR AL MENÚ') : null)),
        el('div', { class: 'lt-menu-profile-panel' },
          el('div', { class: 'lt-menu-profile-name' }, activeProfile ? activeProfile.name : '— SIN PERFIL —'),
          el('div', { class: 'lt-menu-profile-grid' },
            el('div', null, el('span', null, 'PERFILES'), el('strong', null, String(totalProfiles))),
            el('div', null, el('span', null, 'ACTIVO'), el('strong', null, activeProfile ? '✓' : '—')),
            el('div', null, el('span', null, 'MEJOR'), el('strong', null, String(heroBest))),
            el('div', null, el('span', null, 'ELO'), el('strong', null, String(activeProfile?.elo || 1000)))))
      ),
      el('div', { class: 'lt-profile-grid' }, ...cards, addCard)
    )
  ]);
};

SCREENS.menu = (root) => {
  const p = App.active();
  if (!p) return App.show('profile-select');
  const bestOverall = Math.max(0, ...Object.values(p.bestScores || { _: 0 }));
  const games = p.stats?.games || 0;
  const words = p.stats?.totalWords || 0;
  const cardsOwned = (p.owned || []).filter(id => id.startsWith('card-')).length;
  const buttons = Object.values(MODES).map(mode => {
    const best = p.bestScores[mode.id] || 0;
    const today = todayStr();
    const diarioBlocked = mode.id === 'diario' && App.diarioDone[`${today}::${p.id}`];
    const meta = mode.id === 'classic'
      ? ['5 fases', 'boss cada 5', 'draft Letris']
      : mode.id === 'zen'
        ? ['sin game over', 'práctica', 'build libre']
        : mode.id === 'diario'
          ? ['pack fijo', '1 intento', today.slice(5)]
          : ['online', `ELO ${p.elo || 1000}`, 'ataques'];
    return el('button', { class: 'lt-mode-card lt-mode-card--' + mode.id + (diarioBlocked ? ' disabled' : ''),
      onclick: () => diarioBlocked ? alert('Ya jugaste el reto diario hoy.') : startMode(mode.id) },
      el('div', { class: 'lt-mode-card__top' },
        el('div', { class: 'lt-mode-name' }, mode.name),
        el('div', { class: 'lt-mode-best' }, String(best).padStart(5, '0'))),
      el('div', { class: 'lt-mode-sub' }, mode.subtitle),
      el('div', { class: 'lt-mode-tags' }, ...meta.map(t => el('span', null, t))),
      diarioBlocked ? el('div', { class: 'lt-mode-locked' }, 'YA JUGADO HOY') : null
    );
  });
  shellFrame(root, [
    topBar('MENÚ', { coins: p.coins, profile: p.name }),
    el('div', { class: 'lt-screen-body lt-menu' },
      el('section', { class: 'lt-menu-hero' },
        el('div', { class: 'lt-menu-hero__copy' },
          el('div', { class: 'lt-menu-kicker' }, 'RUN PRINCIPAL'),
          el('h1', { class: 'lt-menu-title' }, 'Sube fases, rompe bosses, construye una build.'),
          el('div', { class: 'lt-menu-hero__actions' },
            el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', onclick: () => startMode('classic') }, 'JUGAR CLÁSICO'),
            el('button', { class: 'lt-btn lt-btn--ghost lt-btn--big', onclick: () => App.show('leaderboard', { mode: 'classic', scope: 'global' }) }, 'RANKING GLOBAL'))),
        el('div', { class: 'lt-menu-profile-panel' },
          el('div', { class: 'lt-menu-profile-name' }, p.name),
          el('div', { class: 'lt-menu-profile-grid' },
            el('div', null, el('span', null, 'MEJOR'), el('strong', null, String(bestOverall))),
            el('div', null, el('span', null, 'RACHA'), el('strong', null, '🔥 ' + (p.dailyStreak?.count || 0))),
            el('div', null, el('span', null, 'PARTIDAS'), el('strong', null, String(games))),
            el('div', null, el('span', null, 'PALABRAS'), el('strong', null, String(words))),
            el('div', null, el('span', null, 'CARTAS'), el('strong', null, String(cardsOwned))),
            el('div', null, el('span', null, 'ELO'), el('strong', null, String(p.elo)))))),
      el('div', { class: 'lt-mode-grid' }, ...buttons),
      el('div', { class: 'lt-menu-row' },
        el('button', { class: 'lt-menu-action lt-menu-action--leaderboard', onclick: () => App.show('leaderboard', { mode: 'classic', scope: 'global' }) },
          el('span', null, 'LEADERBOARD'),
          el('strong', null, 'GLOBAL + LOCAL')),
        el('button', { class: 'lt-menu-action lt-menu-action--shop', onclick: () => App.show('shop') },
          el('span', null, 'TIENDA'),
          el('strong', null, `${p.coins} ◈ disponibles`)),
        el('button', { class: 'lt-menu-action', onclick: () => openTutorialModal() },
          el('span', null, 'CÓMO JUGAR'),
          el('strong', null, 'tutorial rápido')),
        el('button', { class: 'lt-menu-action', onclick: () => App.show('profile-select') },
          el('span', null, 'PERFIL'),
          el('strong', null, 'cambiar jugador')),
      ),
      el('div', { class: 'lt-menu-legal' },
        el('button', { class: 'lt-link', onclick: () => App.show('legal', { tab: 'terms' }) }, 'Términos'),
        el('span', null, ' · '),
        el('button', { class: 'lt-link', onclick: () => App.show('legal', { tab: 'privacy' }) }, 'Privacidad'),
        el('span', null, ' · '),
        el('button', { class: 'lt-link', onclick: () => App.show('legal', { tab: 'cookies' }) }, 'Cookies'),
      )
    )
  ]);
};

SCREENS.howto = (root) => {
  // Kept as fallback route; redirect into modal for consistency.
  App.show('menu');
  setTimeout(() => openTutorialModal(), 30);
};

SCREENS.legal = (root, { tab } = {}) => {
  const active = LEGAL_CONTENT[tab] ? tab : 'terms';
  const tabs = el('div', { class: 'lt-legal-tabs' },
    ...Object.keys(LEGAL_CONTENT).map(id => el('button', {
      class: 'lt-legal-tab' + (id === active ? ' active' : ''),
      onclick: () => App.show('legal', { tab: id })
    }, LEGAL_CONTENT[id].title))
  );
  const sec = LEGAL_CONTENT[active];
  shellFrame(root, [
    topBar(sec.title.toUpperCase(), { back: () => App.show(App.activeId ? 'menu' : 'splash') }),
    el('div', { class: 'lt-screen-body lt-legal' },
      tabs,
      el('div', { class: 'lt-card lt-legal-card', html: sec.body })
    )
  ]);
};

SCREENS['duelo-lobby'] = (root, params = {}) => {
  const p = App.active();
  let conn = App._duelConn;
  let phase = 'idle'; // idle | created | queued | matched | error
  let code = '';
  let errorMsg = '';
  let opponentName = '';
  let autoJoinCode = (params.autoJoin || '').toUpperCase().slice(0, 4);

  function buildHero() {
    const wins = p.duelStats?.wins || 0;
    const losses = p.duelStats?.losses || 0;
    const total = wins + losses;
    const ratio = total ? Math.round((wins / total) * 100) + '%' : '—';
    return el('section', { class: 'lt-menu-hero lt-duel-hero' },
      el('div', { class: 'lt-menu-hero__copy lt-duel-hero__copy' },
        el('div', { class: 'lt-menu-kicker' }, 'MULTIJUGADOR ONLINE'),
        el('h1', { class: 'lt-menu-title' }, 'Reta a otro jugador y sube tu ELO.'),
        el('div', { class: 'lt-menu-hero__actions' },
          el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', onclick: random }, 'PARTIDA RÁPIDA'),
          el('button', { class: 'lt-btn lt-btn--ghost lt-btn--big', onclick: create }, 'CREAR SALA'))),
      el('div', { class: 'lt-menu-profile-panel lt-duel-stats-panel' },
        el('div', { class: 'lt-menu-profile-name' }, p.name),
        el('div', { class: 'lt-menu-profile-grid' },
          el('div', null, el('span', null, 'ELO'), el('strong', null, String(p.elo))),
          el('div', null, el('span', null, 'PARTIDAS'), el('strong', null, String(total))),
          el('div', null, el('span', null, 'VICTORIAS'), el('strong', null, String(wins))),
          el('div', null, el('span', null, 'WINRATE'), el('strong', null, ratio))))
    );
  }

  function rerender() {
    let body;
    if (phase === 'matched') {
      const oppElo = App._duelMatch?.opponentElo || 1000;
      body = el('div', { class: 'lt-duel-status lt-duel-status--matched' },
        el('div', { class: 'lt-duel-status__title' }, '¡CONECTADO!'),
        el('div', { class: 'lt-duel-versus' },
          el('div', { class: 'lt-duel-versus__side' },
            el('div', { class: 'lt-duel-versus__name' }, p.name),
            el('div', { class: 'lt-duel-versus__elo' }, String(p.elo))),
          el('div', { class: 'lt-duel-versus__vs' }, 'VS'),
          el('div', { class: 'lt-duel-versus__side' },
            el('div', { class: 'lt-duel-versus__name' }, opponentName),
            el('div', { class: 'lt-duel-versus__elo' }, String(oppElo)))),
        el('div', { class: 'lt-duel-status__hint' }, 'Comenzando...')
      );
    } else if (phase === 'created') {
      const inviteUrl = siteUrl('/?challenge=' + code);
      const inviteText = `⚔️ ${p.name} te reta en Letris.\nÚnete con el código ${code} o pulsa el enlace:`;
      body = el('div', { class: 'lt-duel-status lt-duel-status--waiting' },
        el('div', { class: 'lt-duel-status__title' }, 'TU CÓDIGO DE SALA'),
        el('div', { class: 'lt-duel-code' }, code),
        el('div', { class: 'lt-duel-status__sub' }, 'Comparte el código. Esperando rival...'),
        el('div', { class: 'lt-duel-actions' },
          el('button', { class: 'lt-btn lt-btn--share', onclick: () => shareText({ title: 'Letris · Reto', text: inviteText, url: inviteUrl }) }, '↗ INVITAR'),
          el('button', { class: 'lt-btn lt-btn--ghost', onclick: cancel }, 'CANCELAR'))
      );
    } else if (phase === 'queued') {
      body = el('div', { class: 'lt-duel-status lt-duel-status--waiting' },
        el('div', { class: 'lt-duel-status__title' }, 'BUSCANDO RIVAL...'),
        el('div', { class: 'lt-duel-status__sub' }, 'Cola pública'),
        el('div', { class: 'lt-duel-spinner' }),
        el('button', { class: 'lt-btn lt-btn--ghost', onclick: cancel }, 'CANCELAR')
      );
    } else {
      body = el('div', { class: 'lt-duel-lobby-grid' },
        el('button', { class: 'lt-mode-card lt-duel-card lt-duel-card--create', onclick: create },
          el('div', { class: 'lt-mode-card__top' },
            el('div', { class: 'lt-mode-name' }, 'CREAR SALA'),
            el('div', { class: 'lt-mode-best' }, '◆')),
          el('div', { class: 'lt-mode-sub' }, 'Genera un código y compártelo'),
          el('div', { class: 'lt-mode-tags' },
            el('span', null, 'privada'), el('span', null, '1v1'), el('span', null, 'amigos'))
        ),
        el('div', { class: 'lt-mode-card lt-duel-card lt-duel-card--join' },
          el('div', { class: 'lt-mode-card__top' },
            el('div', { class: 'lt-mode-name' }, 'UNIRSE'),
            el('div', { class: 'lt-mode-best' }, '⇢')),
          el('div', { class: 'lt-mode-sub' }, 'Introduce un código de 4 letras'),
          el('div', { class: 'lt-duel-card__form' },
            el('input', { class: 'lt-input', id: 'duel-code-input', maxlength: '4', placeholder: 'CÓDIGO',
              oninput: (e) => { e.target.value = e.target.value.toUpperCase(); } }),
            el('button', { class: 'lt-btn lt-btn--primary', onclick: join }, 'ENTRAR'))
        ),
        el('button', { class: 'lt-mode-card lt-duel-card lt-duel-card--random', onclick: random },
          el('div', { class: 'lt-mode-card__top' },
            el('div', { class: 'lt-mode-name' }, 'PARTIDA RÁPIDA'),
            el('div', { class: 'lt-mode-best' }, '⚡')),
          el('div', { class: 'lt-mode-sub' }, 'Matchmaking público por ELO'),
          el('div', { class: 'lt-mode-tags' },
            el('span', null, 'pública'), el('span', null, 'ranked'), el('span', null, `ELO ${p.elo}`))
        ),
        el('div', { class: 'lt-mode-card lt-duel-card lt-duel-card--spectate' },
          el('div', { class: 'lt-mode-card__top' },
            el('div', { class: 'lt-mode-name' }, 'ESPECTADOR'),
            el('div', { class: 'lt-mode-best' }, '◉')),
          el('div', { class: 'lt-mode-sub' }, 'Mira una partida en directo'),
          el('div', { class: 'lt-duel-card__form' },
            el('input', { class: 'lt-input', id: 'duel-spec-input', maxlength: '4', placeholder: 'CÓDIGO',
              oninput: (e) => { e.target.value = e.target.value.toUpperCase(); } }),
            el('button', { class: 'lt-btn lt-btn--ghost', onclick: spectate }, 'VER'))
        ),
      );
    }
    const errorBox = errorMsg ? el('div', { class: 'lt-duel-error' }, errorMsg) : null;
    const heroSection = phase === 'idle' ? buildHero() : null;
    root.innerHTML = '';
    shellFrame(root, [
      topBar('DUELO', { back: () => { cancel(); App.show('menu'); }, profile: p.name, coins: p.coins }),
      el('div', { class: 'lt-screen-body lt-duel-lobby' }, heroSection, body, errorBox)
    ]);
  }

  async function ensureConn() {
    if (conn && conn.connected) return conn;
    conn = new DuelConn();
    App._duelConn = conn;
    try {
      await conn.connect(p.name, { elo: p.elo });
    } catch (e) {
      errorMsg = 'No se pudo conectar al servidor.';
      App._duelConn = null;
      conn = null;
      rerender();
      return null;
    }
    conn.on('error', (m) => { errorMsg = m.msg || 'Error'; phase = 'idle'; rerender(); });
    conn.on('created', (m) => { code = m.code; phase = 'created'; rerender(); });
    conn.on('queued', () => { phase = 'queued'; rerender(); });
    conn.on('cancelled', () => { phase = 'idle'; rerender(); });
    conn.on('matched', (m) => {
      opponentName = m.opponentName;
      phase = 'matched';
      App._duelMatch = { seed: m.seed, opponentName: m.opponentName, opponentElo: m.opponentElo || 1000, you: m.you, code: m.code };
      rerender();
      setTimeout(() => App.show('playing', { modeId: 'duelo' }), 700);
    });
    conn.on('opp_left', () => { errorMsg = 'El rival se desconectó.'; phase = 'idle'; rerender(); });
    conn.on('close', () => { App._duelConn = null; });
    return conn;
  }

  async function create() {
    errorMsg = '';
    const c = await ensureConn();
    if (!c) return;
    c.send({ t: 'create' });
  }
  async function join() {
    errorMsg = '';
    const inp = document.getElementById('duel-code-input');
    const v = (inp?.value || '').toUpperCase().trim();
    if (v.length !== 4) { errorMsg = 'Código de 4 caracteres'; rerender(); return; }
    const c = await ensureConn();
    if (!c) return;
    c.send({ t: 'join', code: v });
  }
  async function random() {
    errorMsg = '';
    const c = await ensureConn();
    if (!c) return;
    c.send({ t: 'random' });
  }
  async function spectate() {
    errorMsg = '';
    const inp = document.getElementById('duel-spec-input');
    const v = (inp?.value || '').toUpperCase().trim();
    if (v.length !== 4) { errorMsg = 'Código de 4 caracteres'; rerender(); return; }
    const c = await ensureConn();
    if (!c) return;
    c.on('spectating', (m) => {
      App._spectateInfo = m;
      App.show('duelo-spectate');
    });
    c.send({ t: 'spectate', code: v });
  }
  function cancel() {
    if (conn && conn.connected && (phase === 'queued' || phase === 'created')) {
      conn.send({ t: 'cancel' });
    }
    phase = 'idle';
  }

  async function autoJoinChallenge(c4) {
    errorMsg = '';
    const c = await ensureConn();
    if (!c) return;
    phase = 'queued';
    rerender();
    c.send({ t: 'join', code: c4 });
  }

  rerender();
  if (autoJoinCode && /^[A-Z0-9]{4}$/.test(autoJoinCode)) {
    setTimeout(() => autoJoinChallenge(autoJoinCode), 80);
  }
};

SCREENS.shop = (root) => {
  const p = App.active();
  const groups = [
    ['theme', 'TEMAS', 'Cambian el aspecto global del juego.'],
    ['boost', 'VENTAJAS', 'Mejoras permanentes que alteran el inicio de cada run.'],
    ['card', 'CARTAS', 'Modificadores permanentes para builds y bosses.'],
  ];
  const cards = Object.values(UNLOCKS).map(u => {
    const owned = p.owned.includes(u.id);
    const active = p.activeTheme === u.id && u.kind === 'theme';
    return el('div', { class: 'lt-shop-card lt-shop-card--' + u.kind + (owned ? ' owned' : '') + (active ? ' active' : '') },
      el('div', { class: 'lt-shop-kind' }, u.kind.toUpperCase()),
      u.palette ? el('div', { class: 'lt-shop-swatches' },
        ...u.palette.map(c => el('span', { style: `background:${c}` }))) : null,
      el('div', { class: 'lt-shop-name' }, u.name),
      u.desc ? el('div', { class: 'lt-shop-desc' }, u.desc) : null,
      el('div', { class: 'lt-shop-cost' }, owned ? '✓ EN POSESIÓN' : `◈ ${u.cost}`),
      owned
        ? (u.kind === 'theme'
            ? el('button', { class: 'lt-btn lt-btn--' + (active ? 'ghost' : 'primary'), disabled: active,
                onclick: () => { p.activeTheme = u.id; App.save(); App.applyTheme(); App.show('shop'); } }, active ? 'ACTIVO' : 'USAR')
            : el('div', { class: 'lt-shop-status' }, 'aplicado'))
        : el('button', { class: 'lt-btn lt-btn--primary', disabled: p.coins < u.cost,
            onclick: () => {
              if (p.coins < u.cost) return;
              p.coins -= u.cost;
              p.owned.push(u.id);
              if (u.kind === 'theme') p.activeTheme = u.id;
              App.save(); App.applyTheme(); App.show('shop');
            } }, 'COMPRAR'),
    );
  });
  shellFrame(root, [
    topBar('TIENDA', { back: () => App.show('menu'), coins: p.coins, profile: p.name }),
    el('div', { class: 'lt-screen-body lt-shop-screen' },
      el('div', { class: 'lt-shop-hero' },
        el('div', null,
          el('div', { class: 'lt-menu-kicker' }, 'ORO Y DESBLOQUEOS'),
          el('h1', { class: 'lt-shop-title' }, 'Compra cosas que cambian cómo se siente cada run.')),
        el('div', { class: 'lt-shop-wallet' }, el('span', null, 'SALDO'), el('strong', null, `${p.coins} ◈`))),
      ...groups.map(([kind, title, sub]) => el('section', { class: 'lt-shop-section' },
        el('div', { class: 'lt-shop-section__head' },
          el('h2', null, title),
          el('p', null, sub)),
        el('div', { class: 'lt-shop-grid' }, ...cards.filter(card => card.className.includes('lt-shop-card--' + kind))))),
      el('div', { class: 'lt-card lt-card--hint' }, 'Ganas ◈ por puntos, misiones y bosses. Las ventajas y cartas son permanentes.')
    )
  ]);
};

SCREENS.leaderboard = (root, params = {}) => {
  const modeIds = Object.keys(MODES);
  const activeMode = params.mode && MODES[params.mode] ? params.mode : 'classic';
  const activeScope = params.scope === 'local' ? 'local' : 'global';
  const me = App.active()?.name;

  const tabsMode = el('div', { class: 'lt-lb-tabs' },
    ...modeIds.map(id => el('button', {
      class: 'lt-lb-tab' + (id === activeMode ? ' active' : ''),
      onclick: () => App.show('leaderboard', { mode: id, scope: activeScope })
    }, MODES[id].name))
  );
  const tabsScope = el('div', { class: 'lt-lb-tabs lt-lb-tabs--scope' },
    el('button', { class: 'lt-lb-tab' + (activeScope === 'global' ? ' active' : ''),
      onclick: () => App.show('leaderboard', { mode: activeMode, scope: 'global' }) }, '🌐 GLOBAL'),
    el('button', { class: 'lt-lb-tab' + (activeScope === 'local' ? ' active' : ''),
      onclick: () => App.show('leaderboard', { mode: activeMode, scope: 'local' }) }, '◉ LOCAL'),
  );

  const listEl = el('div', { class: 'lt-lb-single', id: 'lb-list' },
    el('div', { class: 'lt-lb-empty' }, activeScope === 'global' ? 'cargando…' : '— sin entradas —')
  );

  function paint(list, scope) {
    listEl.innerHTML = '';
    if (!list || list.length === 0) {
      listEl.appendChild(el('div', { class: 'lt-lb-empty' }, scope === 'global' ? 'sin partidas registradas' : '— sin entradas —'));
      return;
    }
    list.forEach((e, i) => {
      const isMe = me && e.name === me;
      listEl.appendChild(el('div', { class: 'lt-lb-row' + (isMe ? ' lt-lb-row--me' : '') },
        el('span', { class: 'lt-lb-rank' }, '#' + (i + 1)),
        el('span', { class: 'lt-lb-name' }, e.name),
        el('span', { class: 'lt-lb-score' }, String(e.score)),
        el('span', { class: 'lt-lb-meta' }, 'L' + (e.level || 1))
      ));
    });
  }

  shellFrame(root, [
    topBar('LEADERBOARD', { back: () => App.show('menu'), profile: App.active().name }),
    el('div', { class: 'lt-screen-body lt-lb-screen' },
      tabsMode,
      tabsScope,
      el('div', { class: 'lt-lb-panel' }, listEl,
        el('div', { class: 'lt-lb-foot' },
          activeScope === 'global'
            ? 'Top 50 público · honor system'
            : `Top 10 de este dispositivo`)
      )
    )
  ]);

  if (activeScope === 'local') {
    paint((App.leaderboard[activeMode] || []).slice(0, 10), 'local');
  } else {
    if (App._lbCache && Date.now() - App._lbCache.ts < 30_000) {
      paint((App._lbCache.entries[activeMode] || []).slice(0, 50), 'global');
    } else {
      fetchLeaderboard().then(res => {
        if (!res || !res.entries) {
          paint([], 'global');
          return;
        }
        App._lbCache = { entries: res.entries, ts: Date.now() };
        paint((res.entries[activeMode] || []).slice(0, 50), 'global');
      });
    }
  }
};

SCREENS.playing = (root, { modeId }) => {
  const mode = MODES[modeId];
  const p = App.active();
  document.body.classList.add('lt-screen-playing');
  const gameShell = el('div', { class: 'lt-game-shell' },
    el('header', { class: 'lt-top' },
      logoBlock(),
      el('div', { class: 'lt-mode-badge' }, mode.name),
      el('div', { class: 'lt-controls' },
        mode.config.dailyTargets ? el('button', { id: 'btn-targets', class: 'lt-targets-pill', title: 'Pack del día',
          onclick: () => openTargetsModal() },
          '📋 ', el('span', { id: 'targets-pill-progress' }, '0/0')) : null,
        el('button', { id: 'btn-mute', class: 'lt-icon-btn', title: 'Mute SFX (M)' }, '🔊'),
        el('button', { id: 'btn-music', class: 'lt-icon-btn', title: 'Música (N)' }, '♪'),
        el('button', { id: 'btn-pause', class: 'lt-icon-btn', title: 'Pausa (P)' }, '‖'),
        el('button', { id: 'btn-restart', class: 'lt-icon-btn', title: 'Reiniciar' }, '↻'),
        el('button', { id: 'btn-quit', class: 'lt-icon-btn', title: 'Salir' }, '✕'),
      )
    ),
    el('div', { class: 'lt-stage' },
      el('aside', { class: 'lt-side lt-side--left' },
        el('div', { class: 'lt-card lt-card--status' },
          el('div', { class: 'lt-status__row lt-status__row--top' },
            el('div', { class: 'lt-status__col' },
              el('div', { class: 'lt-status__label' }, 'PUNTOS'),
              el('div', { class: 'lt-score', id: 'score' }, '000000'),
              el('div', { class: 'lt-status__sub' },
                el('span', null, 'BEST '),
                el('span', { class: 'lt-mono', id: 'best' }, '000000'))),
            el('div', { class: 'lt-status__col lt-status__col--coin' },
              el('div', { class: 'lt-status__label' }, '◈ ORO'),
              el('div', { class: 'lt-coin-counter', id: 'coin-counter' },
                el('span', { class: 'lt-mono', id: 'coin-amount' }, String(p.coins))))
          ),
          el('div', { class: 'lt-status__divider' }),
          el('div', { class: 'lt-status__level' },
            el('div', { class: 'lt-status__level-row' },
              el('div', { class: 'lt-level__num', id: 'level-num' }, 'NIVEL 1'),
              el('div', { class: 'lt-status__progress-text', id: 'level-progress-text' }, '0/0')),
            el('div', { class: 'lt-level__bar', id: 'level-bar' }),
            el('div', { class: 'lt-level-goal', id: 'level-goal' }),
            el('div', { class: 'lt-intensity-row' },
              el('span', { class: 'lt-intensity-label' }, 'PRESIÓN'),
              el('div', { class: 'lt-intensity-pips', id: 'intensity-pips' }))
          ),
          el('div', { class: 'lt-time-pill', id: 'time-left', style: 'display:none;' }, '0:00'),
          el('div', { class: 'lt-active-buffs', id: 'active-buffs' })
        ),
        el('div', { class: 'lt-card lt-card--phase' },
          el('div', { class: 'lt-card__title' }, 'FASE'),
          el('div', { class: 'lt-phase-name', id: 'phase-name' }, '—'),
          el('div', { class: 'lt-phase-rule', id: 'phase-rule' }, ''),
          el('div', { class: 'lt-boss-panel', id: 'boss-panel', style: 'display:none;' },
            el('div', { class: 'lt-boss-head' },
              el('span', { class: 'lt-boss-icon', id: 'boss-icon' }, '✦'),
              el('span', { class: 'lt-boss-name', id: 'boss-name' }, 'BOSS')),
            el('div', { class: 'lt-boss-bar' }, el('span', { id: 'boss-bar-fill' })),
            el('div', { class: 'lt-boss-hp', id: 'boss-hp' }, '0/0')
          ),
          el('div', { class: 'lt-mission-panel', id: 'mission-panel' },
            el('div', { class: 'lt-mission-label', id: 'mission-label' }, '—'),
            el('div', { class: 'lt-mission-progress', id: 'mission-progress' }, '0/0'))
        ),
        el('div', { class: 'lt-card lt-card--word' },
          el('div', { class: 'lt-card__title' }, 'PALABRA'),
          el('div', { class: 'lt-word-display', id: 'word-display' },
            el('span', { class: 'lt-word-empty' }, '— — —')),
          el('div', { class: 'lt-actions' },
            el('button', { id: 'btn-send', class: 'lt-btn lt-btn--primary', disabled: true }, 'ENVIAR ↵'),
            el('button', { id: 'btn-back', class: 'lt-btn lt-btn--ghost', disabled: true }, '⌫')),
          el('div', { class: 'lt-actions', style: 'margin-top:6px;' },
            el('button', { id: 'btn-clear', class: 'lt-btn lt-btn--ghost', style: 'flex:1;' }, 'BORRAR')),
          el('div', { class: 'lt-points-preview', id: 'points-preview', style: 'display:none;' }, '+0 pts')
        ),
        el('div', { class: 'lt-card' },
          el('div', { class: 'lt-card__title' }, 'PRÓXIMA'),
          el('div', { class: 'lt-next-row' },
            el('div', { class: 'lt-next-main', id: 'next-main' }),
            el('div', { class: 'lt-next-queue', id: 'next-queue' })))
      ),
      el('div', { class: 'lt-touch-controls lt-touch-controls--bottom', id: 'touch-controls-bottom' },
        ...buildTouchButtons(['left', 'soft', 'right', 'rotate', 'drop'])),
      el('div', { class: 'lt-touch-side lt-touch-side--left', id: 'touch-side-left',
        style: localStorage.getItem('letris.touchSideLeftY') ? `top:${localStorage.getItem('letris.touchSideLeftY')}px` : '' },
        el('div', { class: 'lt-touch-side__handle' }, '⋮'),
        ...buildTouchButtons(['left', 'right'])),
      el('div', { class: 'lt-touch-side lt-touch-side--right', id: 'touch-side-right',
        style: localStorage.getItem('letris.touchSideRightY') ? `top:${localStorage.getItem('letris.touchSideRightY')}px` : '' },
        el('div', { class: 'lt-touch-side__handle' }, '⋮'),
        ...buildTouchButtons(['rotate', 'drop'])),
      el('div', { class: 'lt-board-wrap' },
        el('div', { class: 'lt-board', id: 'board' },
          el('div', { class: 'lt-overflow-line', id: 'overflow-line' }),
          el('div', { class: 'lt-grid', id: 'grid' }),
          el('div', { class: 'lt-flash', id: 'flash', style: 'display:none;' }),
          el('div', { class: 'lt-pause-overlay', id: 'pause-overlay', style: 'display:none;' },
            el('div', { class: 'lt-pause-card' },
              el('div', { class: 'lt-pause__title' }, 'PAUSA'),
              el('button', { id: 'btn-pause-resume', class: 'lt-btn lt-btn--primary' }, 'CONTINUAR'),
              el('button', { class: 'lt-btn lt-btn--ghost', onclick: () => { LetrisGame.abandon(); App.show('menu'); } }, 'SALIR'),
            ))
        )),
      el('aside', { class: 'lt-side lt-side--right' },
        mode.config.dailyTargets ? el('div', { class: 'lt-card lt-card--targets' },
          el('div', { class: 'lt-card__title' }, 'PACK DEL DÍA'),
          el('div', { class: 'lt-targets-progress', id: 'targets-progress' }, '0/0'),
          el('div', { class: 'lt-targets-list', id: 'targets-list' })
        ) : null,
        mode.config.duelMode ? el('div', { class: 'lt-card lt-card--rival' },
          el('div', { class: 'lt-card__title' }, 'RIVAL'),
          el('div', { class: 'lt-rival-name', id: 'rival-name' }, App._duelMatch?.opponentName || '—'),
          el('div', { class: 'lt-rival-stats' },
            el('span', null, 'PTS '), el('span', { class: 'lt-mono', id: 'rival-score' }, '0'),
            el('span', null, ' · L'), el('span', { class: 'lt-mono', id: 'rival-level' }, '1')),
          el('div', { class: 'lt-rival-mini', id: 'rival-mini' }),
          el('div', { class: 'lt-attack-warn', id: 'attack-warn' })
        ) : null,
        el('div', { class: 'lt-card lt-card--letris' },
          el('div', { class: 'lt-card__title' }, 'LETRIS'),
          el('div', { class: 'lt-letris-row', id: 'letris-row' })),
        el('div', { class: 'lt-card lt-card--history' },
          el('div', { class: 'lt-card__title' }, 'HISTORIAL'),
          el('ul', { class: 'lt-history', id: 'history' }))
      )
    )
  );
  shellFrame(root, gameShell);

  const cfg = Object.assign({}, mode.config);
  for (const u of p.owned) {
    const def = UNLOCKS[u];
    if (def && def.kind !== 'theme' && def.apply) def.apply(cfg);
  }
  if (mode.seedFromDate) cfg.seed = hashStr(todayStr());
  if (cfg.duelMode && App._duelMatch) cfg.seed = App._duelMatch.seed;

  let targets = null;
  if (cfg.dailyTargets) {
    targets = pickDailyTargets(hashStr('targets:' + todayStr()), App.dictionary, 5);
    cfg.targetWords = targets;
    App._currentDailyTargets = targets;
    renderDailyTargets(targets, new Set());
  } else {
    App._currentDailyTargets = null;
  }

  cfg.dictionary = App.dictionary;
  cfg.listeners = {
    onWord: ({ word, points }) => {
      p.stats.totalWords++;
      const earned = Math.floor(points * COINS_PER_POINT);
      p.coins += earned;
      App.save();
      bumpCoinCounter(p.coins);
    },
    onCoins: (amount) => {
      p.coins += amount;
      App.save();
      bumpCoinCounter(p.coins);
    },
    onTargetFound: ({ word }) => {
      const found = new Set(LetrisGame.getFoundTargets());
      renderDailyTargets(targets, found);
    },
    onAllTargets: () => {
      // bonus coins by completing the pack
      p.coins += 100;
      App.save();
      bumpCoinCounter(p.coins);
    },
    onGameOver: (score) => onGameOver(modeId, score),
  };

  let duelCleanup = null;
  if (cfg.duelMode) duelCleanup = setupDuelRuntime(cfg);

  LetrisGame.start(cfg);
  LetrisGame.bindUI({
    onRestart: () => {
      duelCleanup?.();
      LetrisGame.abandon();
      if (cfg.duelMode) App.show('menu');
      else App.show('playing', { modeId });
    },
    onQuit: () => {
      duelCleanup?.();
      LetrisGame.abandon();
      App.show('menu');
    },
  });
  attachSideDrag('touch-side-left', 'letris.touchSideLeftY');
  attachSideDrag('touch-side-right', 'letris.touchSideRightY');
};

function setupDuelRuntime(cfg) {
  let conn = App._duelConn;
  if (!conn) return null;
  const p = App.active();
  const baseOnWord = cfg.listeners.onWord;
  const pending = [];
  let stateInterval = null;
  let gameStarted = false;
  let oppOver = false;
  let myOver = false;
  let oppFinalScore = 0;
  let reconnecting = false;
  let reconnectDeadline = 0;
  let reconnectTimer = null;

  function pushAttackWarn(rows, blockers, t0, dur) {
    const warn = document.getElementById('attack-warn');
    if (!warn) return;
    const parts = [];
    if (rows > 0) parts.push(rows + ' fila' + (rows > 1 ? 's' : ''));
    if (blockers > 0) parts.push(blockers + ' bloque' + (blockers > 1 ? 's' : ''));
    const bar = el('div', { class: 'lt-attack-warn__bar' + (blockers > 0 ? ' lt-attack-warn__bar--legendary' : '') },
      el('div', { class: 'lt-attack-warn__label' }, '⚠ ' + parts.join(' + ')),
      el('div', { class: 'lt-attack-warn__fill' })
    );
    warn.appendChild(bar);
    const fill = bar.querySelector('.lt-attack-warn__fill');
    requestAnimationFrame(() => {
      fill.style.transition = `width ${dur}ms linear`;
      fill.style.width = '100%';
    });
    return bar;
  }

  function endDuel(winner, mySc, oppSc) {
    if (!gameStarted) return;
    gameStarted = false;
    if (stateInterval) { clearInterval(stateInterval); stateInterval = null; }
    LetrisGame.abandon();
    // ELO update
    const p = App.active();
    const oppElo = App._duelMatch?.opponentElo || 1000;
    let actual = 0.5;
    if (winner === 'me' || winner === 'me-forfeit') { actual = 1; p.duelStats.wins++; }
    else if (winner === 'opp') { actual = 0; p.duelStats.losses++; }
    const eloBefore = p.elo;
    const { newElo, delta } = updateElo(p.elo, oppElo, actual);
    p.elo = newElo;
    App.save();
    setTimeout(() => App.show('duelo-gameover', {
      winner, mySc, oppSc,
      opponentName: App._duelMatch?.opponentName,
      eloBefore, eloNew: newElo, eloDelta: delta, oppElo,
    }), 600);
  }

  cfg.listeners.onWord = (data) => {
    baseOnWord?.(data);
    // defense: cancel one pending attack with len>=4
    if (data.word.length >= 4 && pending.length > 0) {
      const c = pending.shift();
      if (c.timer) clearTimeout(c.timer);
      c.bar?.classList.add('lt-attack-warn__bar--cancelled');
      setTimeout(() => c.bar?.remove(), 400);
    }
    const rows = attackForWord(data.word.length);
    const blockers = blockersForWord(data.word.length, data.triggeredLegendary);
    if (rows > 0 || blockers > 0) conn.send({ t: 'attack', rows, blockers });
    conn.send({ t: 'word', word: data.word, length: data.word.length });
  };
  cfg.listeners.onGameOver = (score) => {
    if (myOver) return;
    myOver = true;
    const p = App.active();
    p.stats.games++;
    p.stats.totalScore += score;
    App.save();
    conn.send({ t: 'over', score });
    if (oppOver) endDuel(score >= oppFinalScore ? 'me' : 'opp', score, oppFinalScore);
    else endDuel('opp', score, oppFinalScore);
  };

  function handleAttack(m) {
    const dur = 2000;
    const rows = m.rows || 0;
    const blockers = m.blockers || 0;
    const bar = pushAttackWarn(rows, blockers, performance.now(), dur);
    const at = { rows, blockers, bar, timer: null };
    pending.push(at);
    at.timer = setTimeout(() => {
      const idx = pending.indexOf(at);
      if (idx >= 0) {
        pending.splice(idx, 1);
        bar?.remove();
        if (rows > 0) LetrisGame.applyGarbageRows(rows);
        if (blockers > 0) LetrisGame.applyBlockerTiles(blockers);
      }
    }, dur);
  }
  function handleOver(m) {
    oppOver = true;
    oppFinalScore = m.score || 0;
    if (myOver) endDuel(LetrisGame.getScore() >= oppFinalScore ? 'me' : 'opp', LetrisGame.getScore(), oppFinalScore);
    else endDuel('me', LetrisGame.getScore(), oppFinalScore);
  }
  function handleOppLeft() {
    if (!myOver) endDuel('me-forfeit', LetrisGame.getScore(), 0);
  }
  function handleOppDisconnected(m) {
    showReconnectBanner('Rival reconectando…', m.graceMs || 30000);
  }
  function handleOppResumed() {
    hideReconnectBanner();
  }

  function bindConnHandlers(c) {
    c.on('state', renderRivalMini);
    c.on('attack', handleAttack);
    c.on('over', handleOver);
    c.on('opp_left', handleOppLeft);
    c.on('opp_disconnected', handleOppDisconnected);
    c.on('opp_resumed', handleOppResumed);
    c.on('word', () => {});
    c.on('close', () => {
      if (!gameStarted || myOver) return;
      attemptReconnect();
    });
  }

  function attemptReconnect() {
    if (reconnecting) return;
    if (!App._duelMatch?.token) {
      // no token → forfeit
      endDuel('opp', LetrisGame.getScore(), oppFinalScore);
      return;
    }
    reconnecting = true;
    reconnectDeadline = performance.now() + 30000;
    showReconnectBanner('Reconectando…', 30000);
    let attempt = 0;
    const tryOnce = async () => {
      if (!reconnecting) return;
      if (performance.now() >= reconnectDeadline) {
        reconnecting = false;
        hideReconnectBanner();
        endDuel('opp', LetrisGame.getScore(), oppFinalScore);
        return;
      }
      attempt++;
      const newConn = new DuelConn();
      try {
        await newConn.connect(p.name, { elo: p.elo });
        let resolved = false;
        newConn.on('resumed', () => {
          if (resolved) return;
          resolved = true;
          reconnecting = false;
          hideReconnectBanner();
          bindConnHandlers(newConn);
          App._duelConn = newConn;
          conn = newConn;
        });
        newConn.on('error', () => {
          if (resolved) return;
          resolved = true;
          newConn.close();
          reconnecting = false;
          hideReconnectBanner();
          endDuel('opp', LetrisGame.getScore(), oppFinalScore);
        });
        newConn.send({ t: 'resume', token: App._duelMatch.token });
        // if no resumed/error in 5s, retry
        setTimeout(() => {
          if (!resolved && reconnecting) {
            try { newConn.close(); } catch {}
            tryOnce();
          }
        }, 5000);
      } catch (e) {
        // connect failed → backoff
        const wait = Math.min(4000, 800 * attempt);
        reconnectTimer = setTimeout(tryOnce, wait);
      }
    };
    tryOnce();
  }

  bindConnHandlers(conn);

  // start state push
  gameStarted = true;
  stateInterval = setInterval(() => {
    if (LetrisGame.isOver()) return;
    conn.send({ t: 'state', ...LetrisGame.getDuelSnapshot() });
  }, 250);

  return function cleanup() {
    if (stateInterval) clearInterval(stateInterval);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnecting = false;
    hideReconnectBanner();
    pending.forEach(p => { if (p.timer) clearTimeout(p.timer); p.bar?.remove(); });
    if (conn) {
      conn.off('state');
      conn.off('attack');
      conn.off('over');
      conn.off('opp_left');
      conn.off('opp_disconnected');
      conn.off('opp_resumed');
      conn.off('word');
      conn.off('close');
      if (gameStarted && !myOver) conn.send({ t: 'over', score: LetrisGame.getScore() });
    }
    gameStarted = false;
  };
}

function showReconnectBanner(text, durMs) {
  let banner = document.getElementById('lt-reconnect-banner');
  if (!banner) {
    banner = el('div', { class: 'lt-reconnect-banner', id: 'lt-reconnect-banner' });
    document.body.appendChild(banner);
  }
  banner.innerHTML = '';
  banner.appendChild(el('div', { class: 'lt-reconnect-banner__text' }, text));
  const fill = el('div', { class: 'lt-reconnect-banner__fill' });
  banner.appendChild(fill);
  requestAnimationFrame(() => {
    fill.style.transition = `width ${durMs}ms linear`;
    fill.style.width = '100%';
  });
}
function hideReconnectBanner() {
  const banner = document.getElementById('lt-reconnect-banner');
  if (banner) banner.remove();
}

function renderRivalMini(snapshot) {
  const root = document.getElementById('rival-mini');
  if (!root) return;
  const scoreEl = document.getElementById('rival-score');
  const levelEl = document.getElementById('rival-level');
  if (scoreEl) scoreEl.textContent = String(snapshot.score || 0);
  if (levelEl) levelEl.textContent = String(snapshot.level || 1);
  // build/update grid
  let grid = root.firstChild;
  if (!grid) {
    grid = el('div', { class: 'lt-mini-grid' });
    root.appendChild(grid);
    for (let i = 0; i < ROWS * COLS; i++) {
      grid.appendChild(el('div', { class: 'lt-mini-cell' }));
    }
  }
  const cells = grid.children;
  const g = snapshot.grid || '';
  const danger = snapshot.dangerLine || 0;
  for (let i = 0; i < ROWS * COLS; i++) {
    const ch = g[i] || '.';
    const cell = cells[i];
    const r = Math.floor(i / COLS);
    let cls = 'lt-mini-cell';
    if (ch !== '.') cls += ch === '#' ? ' lt-mini-cell--block' : ' lt-mini-cell--filled';
    if (r < danger) cls += ' lt-mini-cell--danger';
    if (cell.className !== cls) cell.className = cls;
  }
}

SCREENS['duelo-spectate'] = (root) => {
  const info = App._spectateInfo;
  const conn = App._duelConn;
  if (!info || !conn) { App.show('duelo-lobby'); return; }
  const names = info.names || ['P1', 'P2'];
  const elos = info.elos || [1000, 1000];
  const wordFeed = [[], []];

  shellFrame(root, [
    topBar('ESPECTADOR · ' + (info.code || ''), { back: () => { conn.off('state'); conn.off('word'); conn.off('attack'); conn.off('over'); App.show('duelo-lobby'); } }),
    el('div', { class: 'lt-screen-body lt-spectate' },
      el('div', { class: 'lt-spectate-grid' },
        ...[0, 1].map(slot => el('div', { class: 'lt-spectate-side' },
          el('div', { class: 'lt-spectate-name' }, names[slot] + '  · ELO ' + elos[slot]),
          el('div', { class: 'lt-spectate-stats' },
            el('span', null, 'PTS '), el('span', { class: 'lt-mono', id: `spec-score-${slot}` }, '0'),
            el('span', null, ' · L'), el('span', { class: 'lt-mono', id: `spec-level-${slot}` }, '1')),
          el('div', { class: 'lt-rival-mini', id: `spec-mini-${slot}` }),
          el('ul', { class: 'lt-spectate-feed', id: `spec-feed-${slot}` })
        ))
      )
    )
  ]);

  function paintMini(slot, snapshot) {
    const root = document.getElementById('spec-mini-' + slot);
    const sc = document.getElementById('spec-score-' + slot);
    const lv = document.getElementById('spec-level-' + slot);
    if (sc) sc.textContent = String(snapshot.score || 0);
    if (lv) lv.textContent = String(snapshot.level || 1);
    if (!root) return;
    let grid = root.firstChild;
    if (!grid) {
      grid = el('div', { class: 'lt-mini-grid' });
      root.appendChild(grid);
      for (let i = 0; i < ROWS * COLS; i++) grid.appendChild(el('div', { class: 'lt-mini-cell' }));
    }
    const cells = grid.children;
    const g = snapshot.grid || '';
    const danger = snapshot.dangerLine || 0;
    for (let i = 0; i < ROWS * COLS; i++) {
      const ch = g[i] || '.';
      const r = Math.floor(i / COLS);
      let cls = 'lt-mini-cell';
      if (ch !== '.') cls += ch === '#' ? ' lt-mini-cell--block' : ' lt-mini-cell--filled';
      if (r < danger) cls += ' lt-mini-cell--danger';
      if (cells[i].className !== cls) cells[i].className = cls;
    }
  }

  conn.on('state', (m) => paintMini(m.from || 0, m));
  conn.on('word', (m) => {
    const slot = m.from || 0;
    const list = document.getElementById('spec-feed-' + slot);
    if (!list) return;
    const li = el('li', null, m.word + ' · ' + (m.length || 0));
    list.insertBefore(li, list.firstChild);
    while (list.children.length > 8) list.lastChild.remove();
  });
  conn.on('attack', () => {});
  conn.on('over', (m) => {
    const slot = m.from;
    const overlay = document.querySelectorAll('.lt-spectate-side')[slot];
    if (overlay) overlay.classList.add('lt-spectate-side--over');
  });
};

SCREENS['duelo-gameover'] = (root, { winner, mySc, oppSc, opponentName, eloBefore, eloNew, eloDelta, oppElo }) => {
  const p = App.active();
  let title = 'EMPATE';
  let resultClass = 'draw';
  let kicker = 'RESULTADO';
  if (winner === 'me') { title = '¡VICTORIA!'; resultClass = 'win'; kicker = 'GANASTE'; }
  else if (winner === 'me-forfeit') { title = 'GANAS POR ABANDONO'; resultClass = 'win'; kicker = 'RIVAL SE FUE'; }
  else if (winner === 'opp') { title = 'DERROTA'; resultClass = 'loss'; kicker = 'PERDISTE'; }
  const eloColor = eloDelta > 0 ? '#06D6A0' : eloDelta < 0 ? '#EF476F' : '#FFD166';
  const eloSign = eloDelta > 0 ? '+' : '';
  async function doShare() {
    const resultEmoji = resultClass === 'win' ? '🏆' : resultClass === 'loss' ? '💀' : '🤝';
    const accent = resultClass === 'win' ? '#06D6A0' : resultClass === 'loss' ? '#EF476F' : '#FFD166';
    const text = `${resultEmoji} Letris Duelo · ${title}\n${p.name} ${mySc} — ${oppSc} ${opponentName || 'RIVAL'}\nELO ${eloBefore} → ${eloNew} (${eloSign}${eloDelta})\n¿Te atreves a duelizar?`;
    const url = siteUrl('/');
    let blob = null;
    try {
      blob = await renderShareCard({
        kicker: 'LETRIS · DUELO',
        title: title.replace(/[¡!]/g, ''),
        bigStat: `${mySc}—${oppSc}`,
        bigStatLabel: `${p.name} VS ${opponentName || 'RIVAL'}`,
        rows: [
          ['ELO ANTES', String(eloBefore)],
          ['ELO AHORA', String(eloNew)],
          ['DELTA', `${eloSign}${eloDelta}`],
          ['RIVAL ELO', String(oppElo)],
        ],
        accent, url,
      });
    } catch {}
    await shareText({ title: 'Letris Duelo', text, url, fileBlob: blob, fileName: 'letris-duelo.png' });
  }
  shellFrame(root, [
    topBar('FIN DEL DUELO', { back: () => App.show('menu'), profile: p.name, coins: p.coins }),
    el('div', { class: 'lt-screen-body lt-duel-gameover' },
      el('section', { class: 'lt-menu-hero lt-duel-hero lt-duel-go-hero lt-duel-go-hero--' + resultClass },
        el('div', { class: 'lt-menu-hero__copy lt-duel-hero__copy' },
          el('div', { class: 'lt-menu-kicker' }, kicker),
          el('h1', { class: 'lt-menu-title' }, title),
          el('div', { class: 'lt-menu-hero__actions' },
            el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', onclick: () => App.show('duelo-lobby') }, 'OTRO DUELO'),
            el('button', { class: 'lt-btn lt-btn--share lt-btn--big', onclick: doShare }, '↗ COMPARTIR'),
            el('button', { class: 'lt-btn lt-btn--ghost lt-btn--big', onclick: () => App.show('menu') }, 'MENÚ'))),
        el('div', { class: 'lt-menu-profile-panel lt-duel-stats-panel' },
          el('div', { class: 'lt-menu-profile-name', style: 'background:' + eloColor }, `ELO ${eloSign}${eloDelta}`),
          el('div', { class: 'lt-menu-profile-grid' },
            el('div', null, el('span', null, 'ELO ANTES'), el('strong', null, String(eloBefore))),
            el('div', null, el('span', null, 'ELO AHORA'), el('strong', { style: 'color:' + eloColor }, String(eloNew))),
            el('div', null, el('span', null, 'TU SCORE'), el('strong', null, String(mySc))),
            el('div', null, el('span', null, 'RIVAL ELO'), el('strong', null, String(oppElo)))))
      ),
      el('div', { class: 'lt-duel-versus lt-duel-versus--final' },
        el('div', { class: 'lt-duel-versus__side' + (mySc >= oppSc ? ' lt-duel-versus__side--winner' : '') },
          el('div', { class: 'lt-duel-versus__name' }, p.name),
          el('div', { class: 'lt-duel-versus__score' }, String(mySc).padStart(6, '0')),
          el('div', { class: 'lt-duel-versus__elo' }, 'ELO ' + p.elo)),
        el('div', { class: 'lt-duel-versus__vs' }, 'VS'),
        el('div', { class: 'lt-duel-versus__side' + (oppSc > mySc ? ' lt-duel-versus__side--winner' : '') },
          el('div', { class: 'lt-duel-versus__name' }, opponentName || 'RIVAL'),
          el('div', { class: 'lt-duel-versus__score' }, String(oppSc).padStart(6, '0')),
          el('div', { class: 'lt-duel-versus__elo' }, 'ELO ' + oppElo))
      )
    )
  ]);
};

function onGameOver(modeId, score) {
  const p = App.active();
  const level = LetrisGame.getLevel();
  p.stats.games++;
  p.stats.totalScore += score;
  if (!p.bestScores[modeId] || score > p.bestScores[modeId]) p.bestScores[modeId] = score;
  App.pushLeaderboard(modeId, { name: p.name, score, level, date: Date.now() });
  let daily = null;
  if (modeId === 'diario' && App._currentDailyTargets) {
    daily = { targets: App._currentDailyTargets, found: LetrisGame.getFoundTargets() };
    const today = todayStr();
    App.diarioDone[`${today}::${p.id}`] = true;
    // streak: count up if completed at least 1 target; reset if gap > 1 day
    if (daily.found.length > 0) {
      const last = p.dailyStreak.lastDate;
      const yesterday = (() => { const d = new Date(today + 'T00:00:00'); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })();
      if (last === today) { /* already counted today */ }
      else if (last === yesterday) p.dailyStreak.count++;
      else p.dailyStreak.count = 1;
      p.dailyStreak.lastDate = today;
      if (p.dailyStreak.count > p.dailyStreak.best) p.dailyStreak.best = p.dailyStreak.count;
    }
  }
  App.save();
  if (score > 0) {
    submitScore({ name: p.name, elo: p.elo || 1000, mode: modeId, score, level })
      .then(res => { if (res && res.rank) App._lastGlobalRank = res.rank; })
      .catch(() => {});
    App._lbCache = null;
  }
  setTimeout(() => App.show('gameover', { modeId, score, level, daily }), 1100);
}

SCREENS.gameover = (root, { modeId, score, level, daily }) => {
  const p = App.active();
  const earned = Math.floor(score * COINS_PER_POINT);
  const isBest = p.bestScores[modeId] === score;
  const modeName = MODES[modeId]?.name || modeId.toUpperCase();
  const dailyBlock = daily ? el('div', { class: 'lt-card', style: 'margin-top:8px;' },
    el('div', { class: 'lt-card__title' }, `PACK DEL DÍA · ${daily.found.length}/${daily.targets.length}`),
    el('div', { class: 'lt-targets-list' },
      ...daily.targets.map(w => {
        const ok = daily.found.includes(w);
        const row = el('div', { class: 'lt-target-row' + (ok ? ' lt-target-row--done' : '') });
        for (const ch of w) row.appendChild(el('span', { class: 'lt-target-cell' }, ch));
        return row;
      })
    )
  ) : null;

  async function doShare() {
    const accent = modeId === 'diario' ? '#FFD166' : modeId === 'duelo' ? '#EF476F' : modeId === 'zen' ? '#6EE7F9' : '#06D6A0';
    let kicker = `LETRIS · ${modeName}`;
    let titleText = p.name;
    let bigStat = String(score);
    let bigStatLabel = 'SCORE';
    let rows = [['NIVEL', level], ['COINS', '+' + earned]];
    let emoji = '';
    let textMsg;
    if (daily) {
      kicker = `LETRIS · DIARIO ${todayStr()}`;
      bigStat = `${daily.found.length}/${daily.targets.length}`;
      bigStatLabel = 'PALABRAS';
      emoji = dailyEmojiBlock(daily.targets, daily.found);
      rows = [['SCORE', String(score)], ['RACHA', `🔥 ${p.dailyStreak.count}`]];
      textMsg = `Letris · Diario ${todayStr()}\n${emoji}\n${daily.found.length}/${daily.targets.length} palabras · ${score} pts · racha 🔥${p.dailyStreak.count}\n¿Lo superas?`;
    } else {
      rows.push(['MODO', modeName]);
      if (isBest) rows.push(['★', 'NUEVO RÉCORD']);
      textMsg = `Hice ${score} pts en Letris (${modeName}, nivel ${level}). ¿Lo superas?`;
    }
    const url = siteUrl('/');
    let blob = null;
    try { blob = await renderShareCard({ kicker, title: titleText, bigStat, bigStatLabel, rows, accent, url, emojiBlock: emoji }); } catch {}
    await shareText({ title: 'Letris', text: textMsg, url, fileBlob: blob, fileName: 'letris-resultado.png' });
  }

  shellFrame(root, [
    topBar('FIN DE PARTIDA', { back: () => App.show('menu'), profile: p.name, coins: p.coins }),
    el('div', { class: 'lt-screen-body lt-gameover' },
      el('div', { class: 'lt-pause-card lt-go-card' },
        el('div', { class: 'lt-pause__title' }, daily && daily.found.length === daily.targets.length ? '¡PACK COMPLETO!' : 'FIN'),
        el('div', { class: 'lt-pause__stats' },
          el('div', null, el('span', null, 'SCORE'), el('strong', { class: 'lt-mono' }, String(score).padStart(6, '0'))),
          el('div', null, el('span', null, 'NIVEL'), el('strong', { class: 'lt-mono' }, String(level))),
          el('div', null, el('span', null, 'COINS'), el('strong', { class: 'lt-mono' }, '+' + earned)),
          daily ? el('div', null, el('span', null, 'RACHA'), el('strong', { class: 'lt-mono' }, '🔥 ' + p.dailyStreak.count)) : null,
          isBest ? el('div', { style: 'background:#06D6A0;color:#000;border:3px solid #000;padding:6px;text-align:center;font-weight:800;letter-spacing:.2em;' }, '★ NUEVO RÉCORD ★') : null
        ),
        dailyBlock,
        el('div', { class: 'lt-actions' },
          el('button', { class: 'lt-btn lt-btn--primary', onclick: () => App.show('playing', { modeId }) }, 'JUGAR DE NUEVO'),
          el('button', { class: 'lt-btn lt-btn--share', onclick: doShare }, '↗ COMPARTIR'),
          el('button', { class: 'lt-btn lt-btn--ghost', onclick: () => App.show('leaderboard') }, 'LEADERBOARD'),
          el('button', { class: 'lt-btn lt-btn--ghost', onclick: () => App.show('menu') }, 'MENÚ')
        )
      )
    )
  ]);
};

// =========================================================================
// HELPERS
// =========================================================================
function startMode(modeId) {
  const mode = MODES[modeId];
  if (mode?.requiresLobby) App.show('duelo-lobby');
  else App.show('playing', { modeId });
}

const TOUCH_BTN_DEFS = {
  left:   { label: '◀',  cls: '',                       title: 'Izquierda',  fn: () => LetrisGame.moveLeft()  },
  right:  { label: '▶',  cls: '',                       title: 'Derecha',    fn: () => LetrisGame.moveRight() },
  soft:   { label: '▼',  cls: '',                       title: 'Bajar',      fn: () => LetrisGame.softDrop()  },
  rotate: { label: '↻',  cls: 'lt-touch-btn--rotate',   title: 'Rotar',      fn: () => LetrisGame.rotate()    },
  drop:   { label: '⤓',  cls: 'lt-touch-btn--drop',     title: 'Caída dura', fn: () => LetrisGame.hardDrop()  },
};
function buildTouchButtons(keys) {
  return keys.map(k => {
    const d = TOUCH_BTN_DEFS[k];
    return el('button', {
      class: 'lt-touch-btn ' + d.cls,
      title: d.title,
      ontouchstart: (e) => { e.preventDefault(); d.fn(); },
      onpointerdown: (e) => { if (e.pointerType !== 'touch') d.fn(); },
    }, d.label);
  });
}

// Drag vertical de paneles laterales para reposicionar (touch)
function attachSideDrag(panelId, storageKey) {
  const panel = document.getElementById(panelId);
  if (!panel) return;
  const handle = panel.querySelector('.lt-touch-side__handle');
  if (!handle) return;
  let dragOffset = 0;
  let active = false;
  handle.addEventListener('pointerdown', (e) => {
    active = true;
    const rect = panel.getBoundingClientRect();
    dragOffset = e.clientY - rect.top;
    handle.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  handle.addEventListener('pointermove', (e) => {
    if (!active) return;
    const newTop = Math.max(8, Math.min(window.innerHeight - panel.offsetHeight - 8, e.clientY - dragOffset));
    panel.style.top = newTop + 'px';
    panel.style.transform = 'none';
  });
  const end = (e) => {
    if (!active) return;
    active = false;
    try { handle.releasePointerCapture(e.pointerId); } catch {}
    localStorage.setItem(storageKey, String(parseInt(panel.style.top, 10) || 0));
  };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
}

function bumpCoinCounter(newTotal) {
  const elAmt = document.getElementById('coin-amount');
  const counter = document.getElementById('coin-counter');
  if (!elAmt || !counter) return;
  const current = parseInt(elAmt.textContent, 10) || 0;
  if (current === newTotal) return;
  // Count-up animado en ~500ms
  const start = current, target = newTotal, dur = 480, t0 = performance.now();
  function step(now) {
    const t = Math.min(1, (now - t0) / dur);
    const eased = 1 - Math.pow(1 - t, 3);
    elAmt.textContent = String(Math.round(start + (target - start) * eased));
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
  counter.classList.remove('lt-coin-counter--bump');
  void counter.offsetWidth;
  counter.classList.add('lt-coin-counter--bump');
  setTimeout(() => counter.classList.remove('lt-coin-counter--bump'), 500);
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const RARE_LETTERS = new Set(['Q','J','Ñ','X','Z','K','W']);
function pickDailyTargets(seed, dictionary, n) {
  const rng = mulberry32(seed);
  const candidates = [];
  for (const w of dictionary) {
    if (w.length < 4 || w.length > 7) continue;
    let rare = 0;
    for (const ch of w) if (RARE_LETTERS.has(ch)) rare++;
    if (rare > 1) continue;
    candidates.push(w);
  }
  // Fisher-Yates partial shuffle for first n picks
  const out = [];
  const seen = new Set();
  let safety = 0;
  while (out.length < n && safety++ < n * 30) {
    const idx = Math.floor(rng() * candidates.length);
    const w = candidates[idx];
    if (seen.has(w)) continue;
    seen.add(w);
    out.push(w);
  }
  return out;
}

function renderDailyTargets(targets, foundSet) {
  const counts = `${foundSet.size}/${targets.length}`;
  const pillProg = document.getElementById('targets-pill-progress');
  if (pillProg) pillProg.textContent = counts;
  // sidebar card (desktop)
  const list = document.getElementById('targets-list');
  const prog = document.getElementById('targets-progress');
  if (list && prog) {
    list.innerHTML = '';
    prog.textContent = counts;
    for (const w of targets) {
      const found = foundSet.has(w);
      const row = el('div', { class: 'lt-target-row' + (found ? ' lt-target-row--done' : '') });
      for (const ch of w) row.appendChild(el('span', { class: 'lt-target-cell' }, ch));
      list.appendChild(row);
    }
  }
  // modal (si abierto)
  const modalList = document.getElementById('targets-modal-list');
  const modalProg = document.getElementById('targets-modal-progress');
  if (modalList && modalProg) {
    modalList.innerHTML = '';
    modalProg.textContent = counts;
    for (const w of targets) {
      const found = foundSet.has(w);
      const row = el('div', { class: 'lt-target-row' + (found ? ' lt-target-row--done' : '') });
      for (const ch of w) row.appendChild(el('span', { class: 'lt-target-cell' }, ch));
      modalList.appendChild(row);
    }
  }
}

function openTargetsModal() {
  if (document.getElementById('lt-targets-modal')) return;
  const targets = App._currentDailyTargets || [];
  const foundSet = new Set(LetrisGame.getFoundTargets());
  const overlay = el('div', { class: 'lt-targets-modal', id: 'lt-targets-modal',
    onclick: (e) => { if (e.target.id === 'lt-targets-modal') closeTargetsModal(); } },
    el('div', { class: 'lt-targets-modal__card' },
      el('div', { class: 'lt-targets-modal__header' },
        el('div', { class: 'lt-targets-modal__title' }, 'PACK DEL DÍA'),
        el('div', { class: 'lt-targets-modal__progress', id: 'targets-modal-progress' }, `${foundSet.size}/${targets.length}`),
        el('button', { class: 'lt-icon-btn', onclick: closeTargetsModal }, '✕')
      ),
      el('div', { class: 'lt-targets-modal__list', id: 'targets-modal-list' })
    )
  );
  document.body.appendChild(overlay);
  renderDailyTargets(targets, foundSet);
}
function closeTargetsModal() {
  document.getElementById('lt-targets-modal')?.remove();
}

// =========================================================================
// LEGAL — términos / privacidad / cookies + gate de aceptación
// =========================================================================
const LEGAL_CONTENT = {
  terms: {
    title: 'Términos',
    body: `
      <h3>1. Aceptación</h3>
      <p>Letris es un juego gratuito de letras inspirado en Tetris. Al jugar aceptas estos términos. Si no estás de acuerdo, no uses el juego.</p>
      <h3>2. Uso permitido</h3>
      <p>El juego se ofrece "tal cual" para uso personal y no comercial. No está permitido explotar fallos, automatizar partidas con bots, ni manipular puntuaciones del leaderboard global.</p>
      <h3>3. Cuentas y perfiles</h3>
      <p>No requerimos registro. Los perfiles se almacenan en tu navegador. Eres responsable de los datos que introduzcas (nombre).</p>
      <h3>4. Contenido del usuario</h3>
      <p>El nick que elijas aparece en el leaderboard global y en los duelos online. No uses contenido ofensivo, insultos ni datos personales de terceros. Nos reservamos el derecho de eliminar entradas inapropiadas.</p>
      <h3>5. Propiedad intelectual</h3>
      <p>El código, arte y diseño de Letris son propiedad de su autor. El diccionario procede de <a href="https://github.com/JorgeDuenasLerin/diccionario-espanol-txt" target="_blank" rel="noopener">JorgeDuenasLerin/diccionario-espanol-txt</a> bajo licencia MIT.</p>
      <h3>6. Garantías</h3>
      <p>El juego se ofrece sin garantías de ningún tipo. No respondemos de pérdidas de progreso por borrar cookies/storage, fallos del navegador o interrupciones del servidor de duelo.</p>
      <h3>7. Cambios</h3>
      <p>Podemos modificar el juego, sus modos, o estos términos en cualquier momento. La versión vigente es la que se muestra aquí.</p>
      <h3>8. Ley aplicable</h3>
      <p>Estos términos se rigen por la ley española salvo cuando la normativa local del usuario imponga lo contrario.</p>
    `,
  },
  privacy: {
    title: 'Privacidad',
    body: `
      <h3>Datos que tratamos</h3>
      <p>Letris busca el mínimo de datos posible:</p>
      <ul>
        <li><strong>Local en tu dispositivo</strong> (localStorage): perfiles, monedas, mejores puntuaciones, temas comprados, cartas, ajustes. Nunca salen de tu navegador salvo que tú compartas la pantalla.</li>
        <li><strong>Servidor de duelo</strong>: cuando juegas online se transmite tu nick, ELO, estado de tablero codificado, ataques y palabras formadas. Nada se persiste salvo el leaderboard.</li>
        <li><strong>Leaderboard global</strong>: al terminar una partida con puntuación &gt; 0 se envía nick + modo + puntos + nivel. Se conserva el top 100 por modo en disco del servidor.</li>
      </ul>
      <h3>Datos que NO tratamos</h3>
      <p>No pedimos email, no integramos analítica de terceros, no usamos cookies de seguimiento, no hay anuncios, no compartimos datos con terceros. No registramos IPs más allá de lo que el propio servidor WebSocket necesita para mantener la conexión activa.</p>
      <h3>Tus derechos</h3>
      <p>Puedes borrar todos tus datos locales en cualquier momento (Ajustes del navegador → borrar datos del sitio). Para que retiremos un nick del leaderboard global, contacta con el responsable del despliegue.</p>
      <h3>Menores</h3>
      <p>El juego no está dirigido específicamente a menores de 14 años, pero el contenido es apto. No recolectamos datos personales identificables.</p>
      <h3>Responsable</h3>
      <p>Este es un proyecto independiente. La identidad del responsable depende del operador del despliegue concreto en el que estás jugando.</p>
    `,
  },
  cookies: {
    title: 'Cookies',
    body: `
      <h3>Resumen</h3>
      <p>Letris <strong>no utiliza cookies HTTP</strong> ni cookies de terceros. No hay píxeles de tracking ni servicios de analítica externos.</p>
      <h3>Almacenamiento local</h3>
      <p>Usamos <code>localStorage</code> del navegador para guardar:</p>
      <ul>
        <li><code>letris.profiles</code> — tus perfiles, coins y mejores puntuaciones.</li>
        <li><code>letris.activeProfile</code> — perfil activo.</li>
        <li><code>letris.leaderboard</code> — top local por modo.</li>
        <li><code>letris.diarioDone</code> — control del reto diario por perfil.</li>
        <li><code>letris.legalAccepted</code> — registro de aceptación legal.</li>
        <li><code>letris.tutorialSeen</code> — control del tutorial inicial.</li>
        <li><code>letris.touchSide*Y</code> — posición personalizada de los paneles táctiles.</li>
      </ul>
      <p>Estos datos no se envían a ningún servidor; viven en tu navegador hasta que los borras.</p>
      <h3>Cómo borrarlos</h3>
      <p>Puedes vaciar localStorage desde las herramientas de desarrollador (Application → Storage) o borrando los datos del sitio desde la configuración del navegador.</p>
    `,
  },
};

function openLegalGateModal(onAccept) {
  if (document.getElementById('lt-legal-gate')) return;
  let acceptedTerms = false, acceptedPrivacy = false;
  const acceptBtn = el('button', { class: 'lt-btn lt-btn--primary lt-btn--big', disabled: true,
    onclick: () => {
      if (!acceptedTerms || !acceptedPrivacy) return;
      localStorage.setItem(STORAGE_KEYS.legalAccepted, LEGAL_VERSION);
      document.getElementById('lt-legal-gate')?.remove();
      onAccept?.();
    }
  }, 'ACEPTAR Y CONTINUAR');

  function refresh() { acceptBtn.disabled = !(acceptedTerms && acceptedPrivacy); }

  const goLegal = (tab) => {
    document.getElementById('lt-legal-gate')?.remove();
    App.show('legal', { tab });
  };
  const cb1 = el('label', { class: 'lt-legal-check' },
    el('input', { type: 'checkbox', onchange: (e) => { acceptedTerms = e.target.checked; refresh(); } }),
    el('span', null, 'He leído y acepto los '),
    el('button', { class: 'lt-link', onclick: () => goLegal('terms') }, 'Términos'),
  );
  const cb2 = el('label', { class: 'lt-legal-check' },
    el('input', { type: 'checkbox', onchange: (e) => { acceptedPrivacy = e.target.checked; refresh(); } }),
    el('span', null, 'He leído la '),
    el('button', { class: 'lt-link', onclick: () => goLegal('privacy') }, 'Privacidad'),
    el('span', null, ' y acepto el uso de '),
    el('button', { class: 'lt-link', onclick: () => goLegal('cookies') }, 'almacenamiento local'),
    el('span', null, '.'),
  );

  const overlay = el('div', { class: 'lt-modal-overlay', id: 'lt-legal-gate' },
    el('div', { class: 'lt-modal-card lt-legal-gate' },
      el('div', { class: 'lt-modal-title' }, 'ANTES DE EMPEZAR'),
      el('p', { class: 'lt-modal-text' },
        'Letris es un juego gratuito y sin anuncios. Para jugar necesitamos tu aceptación de los términos del juego y de cómo usamos los datos básicos (todo se queda en tu navegador, salvo el nick + puntuación que verás en el leaderboard global).'),
      cb1,
      cb2,
      acceptBtn,
    )
  );
  document.body.appendChild(overlay);
}

// =========================================================================
// TUTORIAL — modal interactivo
// =========================================================================
const TUTORIAL_STEPS = [
  {
    title: 'BIENVENIDO A LETRIS',
    text: 'Tetris pero con letras. Las letras caen sobre un tablero 10×20. Tu objetivo: formar palabras en castellano con letras adyacentes y subir de nivel.',
    demo: 'falling',
  },
  {
    title: 'FORMAR UNA PALABRA',
    text: 'Haz clic (o arrastra) sobre letras adyacentes en cualquier dirección, también diagonal, para formar una palabra. Pulsa "Probar" abajo para seleccionar la palabra del ejemplo.',
    demo: 'select',
    interactive: true,
  },
  {
    title: 'ENVIAR Y PUNTUAR',
    text: 'Cuando tu palabra exista en el diccionario, pulsa ENVIAR (o Enter). Las letras desaparecen y suman puntos. Palabras largas y letras raras dan más.',
    demo: 'submit',
  },
  {
    title: 'FUSIÓN DE LETRAS',
    text: 'Si una letra cae justo encima de otra letra idéntica, se fusionan. La de arriba desaparece y la inferior sube de nivel: cambia de color y vale más puntos.',
    demo: 'fusion',
  },
  {
    title: 'CASCADA',
    text: 'Tras eliminar una palabra o fusionar, los bloques superiores caen por gravedad. Esto puede provocar nuevas fusiones automáticas — combo cascada (objetivo de algunos niveles).',
    demo: 'cascade',
  },
  {
    title: 'NIVELES Y PRESIÓN',
    text: 'Cada nivel exige puntos mínimos + un objetivo extra. Cada 5 niveles aparece un boss, y al subir eliges una carta Letris. Cuidado con la línea de peligro: si las letras llegan arriba, fin de partida.',
    demo: 'levels',
  },
  {
    title: 'CONTROLES',
    text: 'En PC: ← → mover · ↓ caída suave · ␣ caída dura · clic/arrastrar para palabra · Enter enviar · ⌫ borrar · Esc limpiar · P pausa. En móvil: usa los botones laterales (puedes arrastrarlos) y toca las letras del tablero.',
    demo: 'controls',
  },
];

function openTutorialModal({ firstTime } = {}) {
  if (document.getElementById('lt-tutorial-modal')) return;
  let step = 0;
  buildDemo._selState = { picked: [] };

  const close = () => {
    localStorage.setItem(STORAGE_KEYS.tutorialSeen, '1');
    document.getElementById('lt-tutorial-modal')?.remove();
  };

  const overlay = el('div', { class: 'lt-modal-overlay', id: 'lt-tutorial-modal',
    onclick: (e) => { if (e.target.id === 'lt-tutorial-modal') close(); }
  });
  const card = el('div', { class: 'lt-modal-card lt-tutorial-card' });
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  function render() {
    const s = TUTORIAL_STEPS[step];
    const total = TUTORIAL_STEPS.length;
    const dots = el('div', { class: 'lt-tut-dots' },
      ...TUTORIAL_STEPS.map((_, i) => el('button', {
        class: 'lt-tut-dot' + (i === step ? ' active' : '') + (i < step ? ' done' : ''),
        onclick: () => { step = i; render(); },
      }))
    );
    const close_btn = el('button', { class: 'lt-icon-btn lt-tut-close', onclick: close, title: 'Cerrar' }, '✕');
    const head = el('div', { class: 'lt-tut-head' },
      el('div', { class: 'lt-tut-step-num' }, `${step + 1}/${total}`),
      el('div', { class: 'lt-tut-title' }, s.title),
      close_btn,
    );
    const demo = el('div', { class: 'lt-tut-demo' }, buildDemo(s.demo, render));
    const text = el('p', { class: 'lt-tut-text' }, s.text);
    const nav = el('div', { class: 'lt-tut-nav' },
      el('button', {
        class: 'lt-btn lt-btn--ghost',
        disabled: step === 0,
        onclick: () => { if (step > 0) { step--; render(); } }
      }, '← ANTERIOR'),
      step < total - 1
        ? el('button', { class: 'lt-btn lt-btn--primary', onclick: () => { step++; render(); } }, 'SIGUIENTE →')
        : el('button', { class: 'lt-btn lt-btn--primary', onclick: () => { close(); if (firstTime) {/* ya estamos en menu */} } }, '¡A JUGAR!')
    );

    card.innerHTML = '';
    card.appendChild(head);
    card.appendChild(dots);
    card.appendChild(demo);
    card.appendChild(text);
    card.appendChild(nav);
  }

  render();
}

// Mini-tablero del tutorial. 6 cols × 4 rows. Letra: { ch, level, hl?, sel? }
function buildMiniGrid(cells, opts = {}) {
  const cols = opts.cols || 6, rows = opts.rows || 4;
  const grid = el('div', { class: 'lt-tut-grid', style: `grid-template-columns: repeat(${cols}, 1fr); --tut-cols:${cols}; --tut-rows:${rows};` });
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = cells[r * cols + c] || null;
      if (!cell) {
        grid.appendChild(el('div', { class: 'lt-tut-cell lt-tut-cell--empty' }));
      } else {
        const lvl = cell.level || 1;
        let cls = `lt-tut-cell lt-tut-cell--l${lvl}`;
        if (cell.hl) cls += ' lt-tut-cell--hl';
        if (cell.sel) cls += ' lt-tut-cell--sel';
        if (cell.fall) cls += ' lt-tut-cell--fall';
        if (cell.danger) cls += ' lt-tut-cell--danger';
        if (cell.cls) cls += ' ' + cell.cls;
        grid.appendChild(el('div', {
          class: cls,
          'data-idx': r * cols + c,
          onclick: cell.onclick,
        }, cell.ch));
      }
    }
  }
  return grid;
}

function buildDemo(kind, rerender) {
  if (kind === 'falling') {
    // Show empty board with one letter falling down col 3
    const cells = new Array(24).fill(null);
    cells[0 * 6 + 2] = { ch: 'L', fall: true };
    cells[3 * 6 + 0] = { ch: 'P' };
    cells[3 * 6 + 1] = { ch: 'A' };
    cells[3 * 6 + 4] = { ch: 'Z' };
    cells[3 * 6 + 5] = { ch: 'O' };
    return el('div', { class: 'lt-tut-demo-wrap' },
      buildMiniGrid(cells),
      el('div', { class: 'lt-tut-cap' }, 'Letras nuevas caen desde arriba ↓'));
  }
  if (kind === 'select') {
    // Interactive: user must click G O L A in order (positions chosen).
    const word = ['G', 'O', 'L', 'A'];
    const positions = { G: 1 * 6 + 1, O: 1 * 6 + 2, L: 2 * 6 + 2, A: 2 * 6 + 3 };
    const state = buildDemo._selState ||= { picked: [] };
    const cells = new Array(24).fill(null);
    cells[0 * 6 + 0] = { ch: 'M' };
    cells[0 * 6 + 4] = { ch: 'B' };
    cells[1 * 6 + 0] = { ch: 'T' };
    cells[1 * 6 + 1] = { ch: 'G' };
    cells[1 * 6 + 2] = { ch: 'O' };
    cells[1 * 6 + 3] = { ch: 'P' };
    cells[1 * 6 + 4] = { ch: 'R' };
    cells[2 * 6 + 1] = { ch: 'I' };
    cells[2 * 6 + 2] = { ch: 'L' };
    cells[2 * 6 + 3] = { ch: 'A' };
    cells[2 * 6 + 4] = { ch: 'S' };
    cells[3 * 6 + 2] = { ch: 'N' };
    cells[3 * 6 + 3] = { ch: 'E' };
    // mark hl/sel based on state
    for (const ch of Object.keys(positions)) {
      const idx = positions[ch];
      if (state.picked.includes(idx)) {
        cells[idx].sel = true;
      } else if (word[state.picked.length] === ch) {
        cells[idx].hl = true;
      }
      const orig = cells[idx].onclick;
      cells[idx].onclick = () => {
        if (word[state.picked.length] === ch && !state.picked.includes(idx)) {
          state.picked.push(idx);
          rerender();
        }
      };
    }
    const formed = state.picked.map(i => cells[i].ch).join('');
    const reset = () => { state.picked = []; rerender(); };
    return el('div', { class: 'lt-tut-demo-wrap' },
      buildMiniGrid(cells),
      el('div', { class: 'lt-tut-word-line' },
        el('span', { class: 'lt-tut-word' }, formed || '— — — —'),
        el('button', { class: 'lt-btn lt-btn--ghost', onclick: reset, disabled: state.picked.length === 0 }, '↺')),
      el('div', { class: 'lt-tut-cap' },
        state.picked.length === 4 ? '¡Palabra "GOLA" formada! En el juego pulsarías ENVIAR.'
        : 'Pulsa las letras en orden: G → O → L → A')
    );
  }
  if (kind === 'submit') {
    const cells = new Array(24).fill(null);
    cells[1 * 6 + 1] = { ch: 'P' };
    cells[1 * 6 + 2] = { ch: 'A' };
    cells[1 * 6 + 3] = { ch: 'N' };
    cells[3 * 6 + 0] = { ch: 'M' };
    cells[3 * 6 + 4] = { ch: 'R' };
    return el('div', { class: 'lt-tut-demo-wrap' },
      buildMiniGrid(cells),
      el('div', { class: 'lt-tut-score-pop' }, '+45 pts'),
      el('div', { class: 'lt-tut-cap' }, '"PAN" → 3 letras × bonus = +45 pts. ¡Las celdas se vacían!'));
  }
  if (kind === 'fusion') {
    // Two letter A's stacking: top A about to merge into bottom A → becomes L2
    const cells = new Array(24).fill(null);
    cells[0 * 6 + 2] = { ch: 'A', fall: true };
    cells[1 * 6 + 2] = { ch: 'A' };
    cells[3 * 6 + 4] = { ch: 'A', level: 2 };
    cells[3 * 6 + 5] = { ch: 'A', level: 3 };
    return el('div', { class: 'lt-tut-demo-wrap' },
      buildMiniGrid(cells),
      el('div', { class: 'lt-tut-cap' }, 'Dos "A" iguales → fusión. La inferior sube de nivel y vale más (L1→L2→L3→MAX).'));
  }
  if (kind === 'cascade') {
    const cells = new Array(24).fill(null);
    cells[0 * 6 + 2] = { ch: 'S', fall: true };
    cells[1 * 6 + 2] = { ch: 'O' };
    cells[2 * 6 + 2] = { ch: 'L' };
    cells[3 * 6 + 0] = { ch: 'M' };
    cells[3 * 6 + 1] = { ch: 'E' };
    cells[3 * 6 + 4] = { ch: 'A' };
    cells[3 * 6 + 5] = { ch: 'R' };
    return el('div', { class: 'lt-tut-demo-wrap' },
      buildMiniGrid(cells),
      el('div', { class: 'lt-tut-cap' }, 'Tras borrar una palabra, los tiles superiores caen. Pueden formar nuevas fusiones automáticas.'));
  }
  if (kind === 'levels') {
    const cells = new Array(24).fill(null);
    // Mark first row danger
    for (let c = 0; c < 6; c++) cells[c] = { ch: '·', danger: true, cls: 'lt-tut-cell--dangerline' };
    cells[2 * 6 + 1] = { ch: 'P' };
    cells[2 * 6 + 2] = { ch: 'A' };
    cells[2 * 6 + 3] = { ch: 'Z', level: 2 };
    cells[3 * 6 + 0] = { ch: 'L' };
    cells[3 * 6 + 4] = { ch: 'O' };
    cells[3 * 6 + 5] = { ch: 'S' };
    return el('div', { class: 'lt-tut-demo-wrap' },
      buildMiniGrid(cells),
      el('div', { class: 'lt-tut-meta-row' },
        el('div', { class: 'lt-tut-meta-pill' }, 'NIVEL 3'),
        el('div', { class: 'lt-tut-meta-pill' }, 'META: 5 palabras 5+'),
        el('div', { class: 'lt-tut-meta-pill lt-tut-meta-pill--danger' }, '⚠ línea peligro')),
      el('div', { class: 'lt-tut-cap' }, 'Cumple los objetivos antes de que las letras alcancen la línea de peligro.'));
  }
  if (kind === 'controls') {
    const keys = [
      { k: '← →', d: 'Mover' },
      { k: '↓', d: 'Caída suave' },
      { k: '␣', d: 'Caída dura' },
      { k: 'clic', d: 'Seleccionar letra' },
      { k: 'Enter', d: 'Enviar palabra' },
      { k: '⌫', d: 'Borrar última' },
      { k: 'Esc', d: 'Limpiar selección' },
      { k: 'P', d: 'Pausa' },
      { k: 'M / N', d: 'Mute / música' },
    ];
    return el('div', { class: 'lt-tut-controls' },
      ...keys.map(k => el('div', { class: 'lt-tut-key-row' },
        el('span', { class: 'lt-tut-key' }, k.k),
        el('span', { class: 'lt-tut-keydesc' }, k.d))));
  }
  return el('div', null, '');
}
